import type { Expression, LogicalDataType } from '@aibi/contracts';
import { assertNever } from '@aibi/shared';
import { isCalculatedMeasure, type SemanticModelIndex } from './model-index.js';

/** Familia de tipos para comparar compatibilidad (entero y decimal son numericos...). */
export type TypeFamily = 'numeric' | 'text' | 'temporal' | 'boolean' | 'other' | 'unknown';

export function typeFamily(type: LogicalDataType): TypeFamily {
  switch (type) {
    case 'integer':
    case 'decimal':
      return 'numeric';
    case 'string':
      return 'text';
    case 'date':
    case 'datetime':
      return 'temporal';
    case 'boolean':
      return 'boolean';
    case 'unknown':
      return 'unknown';
    case 'time':
    case 'duration':
    case 'binary':
    case 'json':
    case 'geography':
      return 'other';
    default:
      return assertNever(type, 'Tipo logico no soportado');
  }
}

/** `true` si dos tipos pueden compararse o unirse; `unknown` es compatible con todo. */
export function compatibleTypes(a: LogicalDataType, b: LogicalDataType): boolean {
  const fa = typeFamily(a);
  const fb = typeFamily(b);
  if (fa === 'unknown' || fb === 'unknown') return true;
  if (fa === 'other' || fb === 'other') return a === b;
  return fa === fb;
}

const ARITHMETIC = new Set(['+', '-', '*', '/', '%']);
const COMPARISON = new Set(['=', '!=', '>', '>=', '<', '<=']);
const DATE_PARTS = new Set(['year', 'quarter', 'month', 'week', 'day']);
const TEXT_FUNCTIONS = new Set(['lower', 'upper', 'trim', 'length']);
const MATH_FUNCTIONS = new Set(['abs', 'round', 'floor', 'ceil', 'power']);

const isStringLiteral = (node: Expression): boolean =>
  node.type === 'literal' && typeof node.value === 'string';

/**
 * Infiere el tipo logico de una expresion y reporta usos incompatibles (aritmetica
 * sobre texto, funciones de fecha sobre numeros...). Es conservadora: si un tipo no se
 * conoce devuelve `unknown` y no reporta nada.
 *
 * @example
 * inferExpressionType({ type: 'function', name: 'year', args: [col] }, index, report) // 'integer'
 */
export function inferExpressionType(
  expression: Expression,
  index: SemanticModelIndex,
  report: (message: string) => void = () => undefined,
): LogicalDataType {
  const expect = (node: Expression, family: TypeFamily, context: string): LogicalDataType => {
    const type = infer(node);
    const actual = typeFamily(type);
    if (actual !== 'unknown' && actual !== family) {
      report(`${context} requiere un valor ${familyLabel(family)} (recibe ${type}).`);
    }
    return type;
  };

  const infer = (node: Expression): LogicalDataType => {
    switch (node.type) {
      case 'literal':
        if (typeof node.value === 'number')
          return Number.isInteger(node.value) ? 'integer' : 'decimal';
        if (typeof node.value === 'string') return 'string';
        if (typeof node.value === 'boolean') return 'boolean';
        return 'unknown';
      case 'column':
        return index.getColumn(node.columnId)?.column.dataType ?? 'unknown';
      case 'measure':
        return measureType(node.measureId);
      case 'unary':
        if (node.operator === 'not') {
          expect(node.operand, 'boolean', 'El operador "not"');
          return 'boolean';
        }
        return expect(node.operand, 'numeric', 'El signo negativo');
      case 'binary':
        return binaryType(node.operator, node.left, node.right);
      case 'function':
        return functionType(node.name, node.args);
      case 'aggregate': {
        if (node.filter) expect(node.filter, 'boolean', 'El filtro de la agregacion');
        if (node.function === 'count' || node.function === 'count-distinct') {
          if (node.argument) infer(node.argument);
          return 'integer';
        }
        if (!node.argument) return 'unknown';
        if (node.function === 'min' || node.function === 'max') return infer(node.argument);
        const argument = expect(node.argument, 'numeric', `La agregacion "${node.function}"`);
        return node.function === 'sum' ? argument : 'decimal';
      }
      case 'case': {
        let result: LogicalDataType = 'unknown';
        const branchTypes = [
          ...node.branches.map((branch) => {
            expect(branch.when, 'boolean', 'La condicion de "case"');
            return infer(branch.then);
          }),
          ...(node.else ? [infer(node.else)] : []),
        ];
        for (const type of branchTypes) {
          if (!compatibleTypes(result, type)) {
            report(`Las ramas de "case" devuelven tipos incompatibles (${result} y ${type}).`);
          } else if (result === 'unknown') result = type;
        }
        return result;
      }
      default:
        return assertNever(node, 'Tipo de expresion no soportado');
    }
  };

  const measureType = (measureId: string): LogicalDataType => {
    const measure = index.getMeasure(measureId);
    if (!measure || isCalculatedMeasure(measure)) return 'unknown';
    if (measure.aggregation === 'count' || measure.aggregation === 'count-distinct') {
      return 'integer';
    }
    const column =
      measure.columnId === undefined ? undefined : index.getColumn(measure.columnId)?.column;
    if (!column) return 'unknown';
    return measure.aggregation === 'avg' || measure.aggregation === 'median'
      ? 'decimal'
      : column.dataType;
  };

  const binaryType = (operator: string, left: Expression, right: Expression): LogicalDataType => {
    if (operator === 'and' || operator === 'or') {
      expect(left, 'boolean', `El operador "${operator}"`);
      expect(right, 'boolean', `El operador "${operator}"`);
      return 'boolean';
    }
    const a = infer(left);
    const b = infer(right);
    if (COMPARISON.has(operator)) {
      const textVsDate =
        (typeFamily(a) === 'temporal' && isStringLiteral(right)) ||
        (typeFamily(b) === 'temporal' && isStringLiteral(left));
      if (!textVsDate && !compatibleTypes(a, b)) {
        report(`No se puede comparar ${a} con ${b} ("${operator}").`);
      }
      return 'boolean';
    }
    if (ARITHMETIC.has(operator)) {
      const fa = typeFamily(a);
      const fb = typeFamily(b);
      if (fa === 'temporal' || fb === 'temporal') return 'unknown';
      for (const [family, type] of [
        [fa, a],
        [fb, b],
      ] as const) {
        if (family !== 'numeric' && family !== 'unknown') {
          report(`El operador "${operator}" requiere valores numericos (recibe ${type}).`);
        }
      }
      if (fa === 'unknown' || fb === 'unknown') return 'unknown';
      return operator === '/' || a === 'decimal' || b === 'decimal' ? 'decimal' : 'integer';
    }
    return 'unknown';
  };

  const functionType = (name: string, args: readonly Expression[]): LogicalDataType => {
    const [first, second, third] = args;
    if (name === 'divide') {
      args.forEach((arg) => expect(arg, 'numeric', 'La funcion "divide"'));
      return 'decimal';
    }
    if (name === 'if') {
      if (first) expect(first, 'boolean', 'La condicion de "if"');
      const whenTrue = second ? infer(second) : 'unknown';
      const whenFalse = third ? infer(third) : 'unknown';
      if (!compatibleTypes(whenTrue, whenFalse)) {
        report(`Las ramas de "if" devuelven tipos incompatibles (${whenTrue} y ${whenFalse}).`);
      }
      return whenTrue === 'unknown' ? whenFalse : whenTrue;
    }
    if (name === 'coalesce') {
      const types = args.map((arg) => infer(arg));
      return types.find((type) => type !== 'unknown') ?? 'unknown';
    }
    if (name === 'nullif') {
      args.slice(1).forEach((arg) => infer(arg));
      return first ? infer(first) : 'unknown';
    }
    if (MATH_FUNCTIONS.has(name)) {
      const types = args.map((arg) => expect(arg, 'numeric', `La funcion "${name}"`));
      if (name === 'floor' || name === 'ceil') return 'integer';
      if (name === 'abs') return types[0] ?? 'unknown';
      return 'decimal';
    }
    if (name === 'concat') {
      args.forEach((arg) => infer(arg));
      return 'string';
    }
    if (TEXT_FUNCTIONS.has(name)) {
      if (first) expect(first, 'text', `La funcion "${name}"`);
      return name === 'length' ? 'integer' : 'string';
    }
    if (name === 'substring') {
      if (first) expect(first, 'text', 'La funcion "substring"');
      args.slice(1).forEach((arg) => expect(arg, 'numeric', 'La funcion "substring"'));
      return 'string';
    }
    if (DATE_PARTS.has(name)) {
      if (first) expect(first, 'temporal', `La funcion "${name}"`);
      return 'integer';
    }
    if (name === 'date-trunc' || name === 'date-diff') {
      let temporal: LogicalDataType = 'unknown';
      for (const arg of args) {
        if (isStringLiteral(arg)) continue;
        const type = expect(arg, 'temporal', `La funcion "${name}"`);
        if (temporal === 'unknown') temporal = type;
      }
      return name === 'date-diff' ? 'integer' : temporal;
    }
    if (name === 'today') return 'date';
    if (name === 'now') return 'datetime';
    args.forEach((arg) => infer(arg));
    return 'unknown';
  };

  return infer(expression);
}

function familyLabel(family: TypeFamily): string {
  switch (family) {
    case 'numeric':
      return 'numerico';
    case 'text':
      return 'de texto';
    case 'temporal':
      return 'de fecha';
    case 'boolean':
      return 'booleano';
    case 'other':
    case 'unknown':
      return family;
    default:
      return assertNever(family, 'Familia no soportada');
  }
}
